import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { LegalModal } from '../components/UI/LegalModal';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { MachhaCharacter } from '../lib/machhaAsset';
import { Button } from '../components/UI/Button';
import { PhoneVerificationFlow } from '../components/Phone/PhoneVerificationFlow';
import { waitlistApi } from '../api/waitlist';
import { WaitlistAnswers } from '../types';
import { clayStaggerContainer, clayStaggerItem } from '../constants/animations';
import { useAuthStore } from '../store/authStore';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './WaitlistOnboardingPage.module.css';

const QUESTIONS: {
  key: keyof WaitlistAnswers;
  question: string;
  options: string[];
}[] = [
  {
    key: 'role',
    question: 'What best describes you?',
    options: [
      'Event Photographer',
      'Brand Marketer',
      'Corporate Comms',
      'Event Manager',
      'Event Organising Company',
      'Other',
    ],
  },
  {
    key: 'eventsPerMonth',
    question: 'How many events do you shoot per month?',
    options: ['1–5', '6–15', '16–30', '30+'],
  },
  {
    key: 'teamSize',
    question: 'How many people are on your team?',
    options: ['Just me', '2–5', '6–20', '20+'],
  },
  {
    key: 'howHeard',
    question: 'How did you hear about Pixel Machha?',
    options: ['Social Media', 'Word of Mouth', 'Search Engine', 'Other'],
  },
];

interface OptionButtonProps {
  label: string;
  selected: boolean;
  onSelect: () => void;
}

const OptionButton: React.FC<OptionButtonProps> = ({ label, selected, onSelect }) => (
  <motion.button
    whileTap={{ scale: 0.96 }}
    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
    onClick={onSelect}
    className={[
      styles['option-btn'],
      selected ? styles['option-btn-selected'] : '',
    ].filter(Boolean).join(' ')}
  >
    <span className={styles['option-label']}>{label}</span>
    <motion.span
      className={styles['option-check']}
      initial={false}
      animate={selected ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.5 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
    >
      <CheckCircle2 className={styles['option-check-icon']} />
    </motion.span>
  </motion.button>
);

const legalLinkStyle: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0,
  fontSize: 'inherit', fontFamily: 'inherit',
  color: 'var(--text-disabled)', textDecoration: 'underline', cursor: 'pointer',
};

export const WaitlistOnboardingPage: React.FC = () => {
  usePageTitle('Apply for Creator Access');
  const navigate = useNavigate();
  const { user, fetchMe } = useAuthStore();
  const [answers, setAnswers] = useState<Partial<WaitlistAnswers>>({});
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);
  const [roleOtherText, setRoleOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [checkingExisting, setCheckingExisting] = useState(true);

  useEffect(() => {
    waitlistApi.getMe()
      .then(() => navigate('/waitlist/pending', { replace: true }))
      .catch(() => setCheckingExisting(false));
  }, [navigate]);

  const allAnswered = QUESTIONS.every((q) => {
    if (q.key === 'role' && answers.role === 'Other') return roleOtherText.trim().length > 0;
    return !!answers[q.key];
  });
  const phoneVerified = Boolean(user?.phone && user?.phoneVerifiedAt);
  const canSubmit = allAnswered && phoneVerified;

  const handlePhoneVerified = async () => {
    await fetchMe();
  };

  const handleSelect = (key: keyof WaitlistAnswers, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const role = answers.role === 'Other' ? roleOtherText.trim() : answers.role;
      await waitlistApi.submit({
        ...(answers as WaitlistAnswers),
        role: role!,
        phone: user!.phone!,
      });
      navigate('/waitlist/pending', { replace: true });
    } catch (err) {
      const data = err instanceof AxiosError
        ? (err.response?.data as { error?: string } | undefined)
        : undefined;
      setSubmitError(data?.error ?? 'Failed to submit. Please try again.');
      setSubmitting(false);
    }
  };

  if (checkingExisting) {
    return (
      <div className={styles['loading-screen']}>
        <Loader2 className={styles['loading-spinner']} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Mascot — bottom-left, large, desktop only */}
      <div className={styles['mascot-desktop']}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: [0, -10, 0] }}
          transition={{
            opacity: { duration: 0.5, delay: 0.3 },
            y: { repeat: Infinity, duration: 4, ease: 'easeInOut', delay: 0.8 },
          }}
        >
          <MachhaCharacter state="Onboarding" part="body" size={220} />
        </motion.div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Header */}
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/* Mobile-only mascot (small, inline) */}
          <div className={styles['mascot-mobile']}>
            <MachhaCharacter state="Onboarding" part="body" size={90} />
          </div>
          <h1 className={styles.heading}>
            Join the Private Beta
          </h1>
          <p className={styles.subheading}>
            {user?.name ? `Hey ${user.name.split(' ')[0]}! ` : ''}Tell us a bit about yourself and we'll review your application.
          </p>
        </motion.div>

        {/* Questions */}
        <motion.div
          className={styles.questions}
          variants={clayStaggerContainer}
          initial="hidden"
          animate="visible"
        >
          {QUESTIONS.map((q) => (
            <motion.div
              key={q.key}
              variants={clayStaggerItem}
              className={styles['question-card']}
            >
              <p className={styles['question-text']}>
                {q.question}
              </p>
              <div className={styles['options-grid']}>
                {q.options.map((option) => (
                  <OptionButton
                    key={option}
                    label={option}
                    selected={answers[q.key] === option}
                    onSelect={() => handleSelect(q.key, option)}
                  />
                ))}
              </div>

              {/* "Other" free-text input — role question only */}
              <AnimatePresence>
                {q.key === 'role' && answers.role === 'Other' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    transition={{ type: 'spring', stiffness: 340, damping: 30 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <input
                      autoFocus
                      type="text"
                      value={roleOtherText}
                      onChange={(e) => setRoleOtherText(e.target.value)}
                      placeholder="Tell us what describes you best…"
                      className={styles['other-input']}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}

          {/* Phone — the backend only accepts the number the user verified over OTP */}
          <motion.div variants={clayStaggerItem} className={styles['question-card']}>
            {phoneVerified ? (
              <>
                <p className={styles['phone-header']}>Your phone number</p>
                <div className={styles['phone-verified']}>
                  <CheckCircle2 className={styles['phone-verified-icon']} />
                  <span>{user!.phone}</span>
                </div>
              </>
            ) : (
              <PhoneVerificationFlow onVerified={handlePhoneVerified} />
            )}
          </motion.div>

          {/* Submit */}
          <motion.div variants={clayStaggerItem} className={styles['submit-area']}>
            {submitError && (
              <p style={{ color: 'var(--clay-orange)', fontSize: '0.875rem', marginBottom: '0.5rem', textAlign: 'center' }}>
                {submitError}
              </p>
            )}
            <div className={styles['submit-wrapper']}>
              <Button
                variant="primary"
                size="lg"
                loading={submitting}
                disabled={!canSubmit || submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Submitting…' : 'Join the Private Beta →'}
              </Button>
            </div>
            <p style={{ color: 'var(--text-disabled)', fontSize: '0.6875rem', marginTop: '0.75rem', textAlign: 'center' }}>
              By joining, you agree to our{' '}
              <button style={legalLinkStyle} onClick={() => setLegalModal('terms')}>Terms of Service</button>
              {' '}and{' '}
              <button style={legalLinkStyle} onClick={() => setLegalModal('privacy')}>Privacy Policy</button>.
            </p>
          </motion.div>
          {legalModal && <LegalModal type={legalModal} isOpen onClose={() => setLegalModal(null)} />}
        </motion.div>
      </div>
    </div>
  );
};
